import { useState, type ChangeEvent } from 'react';
import { clamp, parseNumeric, roundToStep, snapStep } from '../lib/format';

export interface NumericInputProps {
  id: string;
  value: number | null;
  onChange: (v: number | null) => void;
  onBlur: () => void;
  error: string | null;
  min?: number;
  max?: number;
  step?: number;
  stepper?: boolean;
  /** Federal-bracket-style walk: stepper snaps to these values instead of incrementing by `step`. */
  snapTo?: number[];
  prefix?: string;
  suffix?: string;
  ariaLabel?: string;
  /** See CalculatorConfig.stepperLayout. */
  stepperLayout?: 'flank' | 'arrows' | 'stacked';
  /** See CalculatorConfig.stepperLabels. */
  stepperLabels?: { increase: string; decrease: string };
  /** See CalculatorConfig.inputStyle. */
  inputStyle?: 'box' | 'inline' | 'fit';
  /** See CalculatorConfig.invalidInput. 'hold' skips the clamp on blur. */
  invalidInput?: 'clamp' | 'hold';
}

const groupedFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

/**
 * Outlined arrow, drawn inline (no icon-font dependency inside the widget).
 * Both outlines are drawn; the stylesheet shows the square one when the
 * calculator's Style is Square and the circle otherwise (Brad, 2026-09-28).
 */
function CircleArrow({ direction }: { direction: 'up' | 'down' }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false">
      <circle
        className="hsab-calc__stepper-arrow-ring hsab-calc__stepper-arrow-ring--round"
        cx="12"
        cy="12"
        r="10.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <rect
        className="hsab-calc__stepper-arrow-ring hsab-calc__stepper-arrow-ring--square"
        x="1.5"
        y="1.5"
        width="21"
        height="21"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <path
        d={direction === 'up' ? 'M12 17.5V7M7.5 11.5 12 7l4.5 4.5' : 'M12 6.5V17M7.5 12.5 12 17l4.5-4.5'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Chevron for the 'stacked' steppers (HSA Savings Odonlow). */
function Chevron({ direction }: { direction: 'up' | 'down' }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false">
      <path
        d={direction === 'up' ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6'}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function NumericInput({
  id,
  value,
  onChange,
  onBlur,
  error,
  min,
  max,
  step = 1,
  stepper,
  snapTo,
  prefix,
  suffix,
  ariaLabel,
  stepperLayout = 'flank',
  stepperLabels = { increase: 'Increase', decrease: 'Decrease' },
  inputStyle = 'box',
  invalidInput = 'clamp',
}: NumericInputProps) {
  const inline = inputStyle === 'inline';
  const fit = inputStyle === 'fit';
  const [focused, setFocused] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(parseNumeric(e.target.value));
  };

  const handleBlur = () => {
    setFocused(false);
    if (value !== null && invalidInput === 'clamp') {
      onChange(clamp(value, min, max));
    }
    // 'hold' (prod's legacy behavior): an emptied field becomes 0 and is then
    // validated like any typed value, so e.g. empty years shows the "between
    // 1 and 30" message rather than a generic required-field one.
    if (value === null && invalidInput === 'hold') {
      onChange(0);
    }
    onBlur();
  };

  const stepValue = (direction: 1 | -1) => {
    if (snapTo && snapTo.length > 0) {
      onChange(snapStep(snapTo, value, direction));
      return;
    }
    onChange(clamp(roundToStep((value ?? 0) + direction * step, step), min, max));
  };

  // Decrease sits to the left of the input, increase to the right (Brad,
  // 2026-09-22) — previously both buttons trailed the input as a single
  // group. Prefix/suffix ($ / %) are now layered inside the input box
  // itself via absolute positioning, not rendered as separate text beside
  // it — the input gets matching inline padding so its own text doesn't
  // run under them.
  const arrows = stepper && stepperLayout === 'arrows';
  const stacked = stepper && stepperLayout === 'stacked';
  const flank = stepper && !arrows && !stacked;

  // Inline style (Spotlight, Brad 2026-10-01): the value reads as bold text
  // ("$2,578") instead of sitting in a box, so it shows thousands separators
  // while not being edited and sizes itself to its own characters.
  const display =
    inline && !focused && value !== null ? groupedFormatter.format(value) : value === null ? '' : String(value);
  // Digits are 1ch (tabular figures); separators and the decimal point are
  // much narrower, so they're counted at ~0.35ch to keep the arrows snug.
  const narrow = (display.match(/[,.]/g) ?? []).length;
  // 'fit' (Odonlow, Brad 2026-10-19) sizes the same way but keeps the box and
  // the plain number, so the "$" can sit right against the value.
  const inlineWidth = inline || fit
    ? { width: `calc(${Math.max(display.length - narrow, 1)}ch + ${narrow * 0.35}ch + 2px)` }
    : undefined;

  return (
    <span
      className={
        'hsab-calc__numeric-input' +
        (arrows ? ' hsab-calc__numeric-input--arrows' : '') +
        (stacked ? ' hsab-calc__numeric-input--stacked' : '') +
        (inline ? ' hsab-calc__numeric-input--inline' : '') +
        (fit ? ' hsab-calc__numeric-input--fit' : '')
      }
    >
      {flank && (
        <button type="button" className="hsab-calc__stepper-btn" aria-label={stepperLabels.decrease} onClick={() => stepValue(-1)}>
          &minus;
        </button>
      )}
      <span className="hsab-calc__numeric-input-wrap">
        {prefix && (
          <span className="hsab-calc__numeric-input-affix hsab-calc__numeric-input-affix--prefix" aria-hidden="true">
            {prefix}
          </span>
        )}
        <input
          id={id}
          type="text"
          inputMode="decimal"
          className={
            'hsab-calc__text-input' +
            (prefix ? ' hsab-calc__text-input--has-prefix' : '') +
            (suffix ? ' hsab-calc__text-input--has-suffix' : '')
          }
          value={display}
          style={inlineWidth}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          aria-label={ariaLabel}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        {suffix && (
          <span className="hsab-calc__numeric-input-affix hsab-calc__numeric-input-affix--suffix" aria-hidden="true">
            {suffix}
          </span>
        )}
      </span>
      {flank && (
        <button type="button" className="hsab-calc__stepper-btn" aria-label={stepperLabels.increase} onClick={() => stepValue(1)}>
          +
        </button>
      )}
      {stacked && (
        // Up over down, inside the field's right edge (HSA Savings Odonlow).
        <span className="hsab-calc__stepper-stack">
          <button
            type="button"
            className="hsab-calc__stepper-stack-btn"
            aria-label={stepperLabels.increase}
            onClick={() => stepValue(1)}
          >
            <Chevron direction="up" />
          </button>
          <button
            type="button"
            className="hsab-calc__stepper-stack-btn"
            aria-label={stepperLabels.decrease}
            onClick={() => stepValue(-1)}
          >
            <Chevron direction="down" />
          </button>
        </span>
      )}
      {arrows && (
        <span className="hsab-calc__stepper-arrows">
          <button type="button" className="hsab-calc__stepper-arrow" aria-label={stepperLabels.increase} onClick={() => stepValue(1)}>
            <CircleArrow direction="up" />
          </button>
          <button type="button" className="hsab-calc__stepper-arrow" aria-label={stepperLabels.decrease} onClick={() => stepValue(-1)}>
            <CircleArrow direction="down" />
          </button>
        </span>
      )}
    </span>
  );
}
